import { DateRange } from '@mui/lab/DateRangePicker';
import dayjs from 'dayjs';
import { endOfMonth, startOfMonth } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';

const STORAGE_KEY = 'usageDateRange';

function getDefaultRange(): DateRange<Date> {
  const now = new Date();
  const start = startOfMonth(now);
  start.setHours(0, 0, 0, 0);
  const end = endOfMonth(now);
  end.setHours(23, 59, 59, 0);
  return [start, end];
}

function readStoredRange(): DateRange<Date> {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      const { start: startStr, end: endStr } = JSON.parse(stored);
      const start = dayjs(startStr);
      const end = dayjs(endStr);
      if (start.isValid() && end.isValid()) {
        return [
          start.set('hour', 0).set('minute', 0).set('second', 0).toDate(),
          end.set('hour', 23).set('minute', 59).set('second', 59).toDate(),
        ];
      }
    }
  } catch {
    // ignore corrupted storage
  }
  return getDefaultRange();
}

/**
 * Persists the usage graph date range in sessionStorage so it survives
 * navigation between properties and units within the same browser session.
 */
export function usePersistedDateRange() {
  const [range, setRangeState] = useState<DateRange<Date>>(readStoredRange);

  const setRange = useCallback((newRange: DateRange<Date>) => {
    if (!newRange[0] || !newRange[1]) return;
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        start: dayjs(newRange[0]).format('YYYY-MM-DD'),
        end: dayjs(newRange[1]).format('YYYY-MM-DD'),
      }),
    );
    setRangeState([
      dayjs(newRange[0]).set('hour', 0).set('minute', 0).set('second', 0).toDate(),
      dayjs(newRange[1]).set('hour', 23).set('minute', 59).set('second', 59).toDate(),
    ]);
  }, []);

  const moveByMonth = useMemo(() => {
    const start = dayjs(range[0]);
    const end = dayjs(range[1]);
    return start.isSame(start.startOf('month'), 'day') && end.isSame(end.endOf('month'), 'day');
  }, [range]);

  return { range, setRange, moveByMonth };
}
